import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileCheck2,
  FolderKanban,
  Maximize2,
  Menu,
  Printer,
  RotateCcw,
  Sparkles,
  X,
  type LucideIcon,
} from "lucide-react";
import SEO from "@/components/SEO";
import { trackClick, trackEvent } from "@/utils/posthog";

export type SlideTone = "dark" | "paper" | "blue" | "orange";
export type SlideKind =
  | "cover"
  | "outcomes"
  | "roadmap"
  | "activity"
  | "prompt"
  | "checkpoint"
  | "custom"
  | "finish";

export interface GuidePrompt {
  label: string;
  value: string;
}

export interface GuideLink {
  label: string;
  href: string;
  download?: boolean;
  eventName?: string;
  external?: boolean;
}

export interface GuideImage {
  src: string;
  alt: string;
  caption: string;
  label?: string;
}

export type GuideAction = string | { text: string; subSteps: string[] };

export interface GuideReference {
  title?: string;
  headers: string[];
  rows: string[][];
}

export interface GuideStep {
  id: string;
  title: string;
  description: string;
  duration: string;
  whyItMatters: string;
  actions: GuideAction[];
  checkpoint: string;
  icon: LucideIcon;
  prompts?: GuidePrompt[];
  deliverables?: string[];
  images?: GuideImage[];
  note?: string;
  links?: GuideLink[];
  reference?: GuideReference;
}

export interface GuideSlide {
  id: string;
  title: string;
  kind: SlideKind;
  tone: SlideTone;
  stepId?: string;
  eyebrow?: string;
  heading?: string;
  actionIndexes?: number[];
  promptIndexes?: number[];
  showWhy?: boolean;
  showNote?: boolean;
  showLinks?: boolean;
  showImages?: boolean;
  showDeliverables?: boolean;
  showReference?: boolean;
  showCheckpoint?: boolean;
  completesStep?: boolean;
  compactLayout?: boolean;
  nextLabel?: string;
}

export interface FinishCard {
  eyebrow: string;
  title: string;
  body: string;
  href: string;
}

export interface GuideBranding {
  rootClassName?: string;
  exit?: { href: string; label: string };
  contentsTitle?: string;
  /** Tone of the persistent header and footer chrome. Slides keep their own tones. */
  chrome?: "dark" | "light";
  logo?: {
    src: string;
    alt: string;
    href?: string;
    ariaLabel?: string;
    imageClassName?: string;
    wrapperClassName?: string;
  };
  paperBackdrop?: string;
  darkBackdrop?: string;
}

interface CustomSlideContext {
  light: boolean;
  printMode: boolean;
}

interface SlideGuideProps {
  name: string;
  trackingName: string;
  canonicalPath: string;
  storageKey: string;
  slideStorageKey: string;
  additionalStorageKeys?: readonly string[];
  seo: {
    title: string;
    description: string;
    keywords: string[];
    image?: string;
    author?: string;
  };
  schema: {
    description: string;
    totalTime: string;
    supplies?: string[];
  };
  cover: {
    kicker?: string;
    title: string;
    accent: string;
    accentClassName?: string;
    description: string;
    badges: string[];
    finishLineTitle: string;
    finishLineBody: string;
    hideFinishLineEyebrow?: boolean;
    minimal?: boolean;
    logo?: {
      src: string;
      alt: string;
      imageClassName?: string;
      wrapperClassName?: string;
    };
  };
  outcomes: {
    eyebrow: string;
    heading: string;
    items: string[];
    summary: string;
  };
  roadmapHeading: string;
  steps: GuideStep[];
  slides: GuideSlide[];
  finish: {
    eyebrow: string;
    heading: string;
    body: string;
    cards: FinishCard[];
  };
  branding?: GuideBranding;
  contentsExtras?: Array<{ slideId: string; label: string }>;
  centerSlideContent?: boolean;
  /** Minimal audience canvas; presenter controls remain available through C and F. */
  audienceView?: boolean;
  renderCustomSlide?: (slide: GuideSlide, context: CustomSlideContext) => ReactNode;
}

const EMPTY_STORAGE_KEYS: readonly string[] = [];

const TONE_CLASSES: Record<SlideTone, string> = {
  dark: "bg-[#020817] text-white",
  paper: "bg-[#faf8f5] text-slate-950",
  blue: "bg-[#0a1f44] text-white",
  orange: "bg-[#c8550a] text-white",
};

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const ActionList = ({
  step,
  indexes,
  light,
}: {
  step: GuideStep;
  indexes?: number[];
  light: boolean;
}) => {
  const actions = (indexes ?? step.actions.map((_, index) => index))
    .map((index) => ({ index, value: step.actions[index] }))
    .filter((action) => action.value);

  return (
    <ol className="space-y-3 sm:space-y-4">
      {actions.map(({ index, value }) => (
        <li key={`${step.id}-${index}`} className="flex items-start gap-3 sm:gap-4">
          <span
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${
              light
                ? "bg-slate-950 text-white"
                : "bg-white/10 text-orange-300 ring-1 ring-white/15"
            }`}
          >
            {index + 1}
          </span>
          <span
            className={`pt-0.5 text-base leading-relaxed sm:text-lg ${
              light ? "text-slate-700" : "text-slate-200"
            }`}
          >
            {typeof value === "string" ? value : (
              <>
                {value.text}
                <ul className={`mt-2 space-y-1.5 pl-5 text-sm ${light ? "text-slate-600" : "text-slate-400"}`}>
                  {value.subSteps.map((subStep) => <li key={subStep} className="list-disc">{subStep}</li>)}
                </ul>
              </>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
};

const PromptCard = ({
  prompt,
  stepId,
  trackingName,
  printMode,
  light,
  compact,
}: {
  prompt: GuidePrompt;
  stepId: string;
  trackingName: string;
  printMode: boolean;
  light: boolean;
  compact: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyText(prompt.value);
      setCopied(true);
      trackEvent(`${trackingName} Prompt Copied`, {
        step: stepId,
        prompt: prompt.label,
      });
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={`overflow-hidden rounded-2xl border shadow-2xl ${light ? "border-slate-200 bg-white text-slate-950 shadow-slate-950/10" : "border-white/15 bg-[#020817] text-white shadow-black/20"}`}>
      <div className={`flex items-center justify-between gap-4 border-b px-4 py-3 sm:px-5 ${light ? "border-slate-200" : "border-white/10"}`}>
        <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-orange-700" : "text-orange-300"}`}>
          {prompt.label}
        </p>
        {!printMode && (
          <button
            type="button"
            onClick={() => void handleCopy()}
            className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${light ? "border-slate-200 bg-slate-950 text-white hover:border-orange-400 hover:bg-orange-600" : "border-white/15 bg-white/[0.06] text-white hover:border-orange-400 hover:bg-white/10"}`}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
      <pre className={`overflow-auto whitespace-pre-wrap px-4 py-5 font-mono text-[13px] leading-6 sm:px-5 sm:text-sm ${compact ? "max-h-[34svh] lg:max-h-[38vh]" : "max-h-[48svh] lg:max-h-[52vh]"} ${light ? "bg-slate-50 text-slate-800" : "text-slate-100"}`}>
        {prompt.value}
      </pre>
    </div>
  );
};

const ImageGrid = ({ images }: { images: GuideImage[] }) => (
  <div className={`grid gap-4 ${images.length > 1 ? "md:grid-cols-2" : ""}`}>
    {images.map((image) => (
      <figure
        key={`${image.src}-${image.label ?? image.alt}`}
        className="overflow-hidden rounded-2xl border border-slate-950/10 bg-white shadow-2xl shadow-slate-950/15"
      >
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
          <div className="flex gap-2" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          {image.label && (
            <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-600">
              {image.label}
            </span>
          )}
        </div>
        <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className="h-auto w-full" />
        <figcaption className="border-t border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-600">
          {image.caption}
        </figcaption>
      </figure>
    ))}
  </div>
);

const Deliverables = ({ items, light }: { items: string[]; light: boolean }) => (
  <div className={`rounded-2xl border p-5 ${light ? "border-slate-200 bg-white" : "border-white/15 bg-white/[0.06]"}`}>
    <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-orange-700" : "text-orange-300"}`}>
      What you leave with
    </p>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3">
          <FileCheck2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" />
          <span className={`text-sm font-semibold leading-relaxed ${light ? "text-slate-700" : "text-slate-200"}`}>
            {item}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const ReferenceTable = ({ reference, light }: { reference: GuideReference; light: boolean }) => (
  <div className={`overflow-hidden rounded-2xl border ${light ? "border-slate-200 bg-white" : "border-white/15 bg-white/[0.06]"}`}>
    <p className={`px-4 py-3 text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-orange-700" : "text-orange-300"}`}>
      {reference.title ?? "Quick reference"}
    </p>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-xs sm:min-w-[420px] sm:text-sm">
        <thead className={light ? "bg-slate-100 text-slate-700" : "bg-white/[0.06] text-slate-200"}>
          <tr>{reference.headers.map((header) => <th key={header} className="border-t border-inherit px-3 py-3 font-bold sm:px-4">{header}</th>)}</tr>
        </thead>
        <tbody>
          {reference.rows.map((row, rowIndex) => (
            <tr key={`${row[0]}-${rowIndex}`} className={light ? "border-t border-slate-200 text-slate-600" : "border-t border-white/10 text-slate-300"}>
              {row.map((cell, cellIndex) => <td key={`${cell}-${cellIndex}`} className="break-words px-3 py-3 align-top leading-relaxed sm:px-4">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

interface SlideContentProps {
  slide: GuideSlide;
  steps: GuideStep[];
  trackingName: string;
  cover: SlideGuideProps["cover"];
  outcomes: SlideGuideProps["outcomes"];
  roadmapHeading: string;
  finish: SlideGuideProps["finish"];
  onStart: () => void;
  onGoToStep: (stepId: string) => void;
  renderCustomSlide?: SlideGuideProps["renderCustomSlide"];
  printMode?: boolean;
}

export const GuidePromptSlide = ({
  slide, step, stepNumber = 1, stepCount = 1, trackingName, printMode = false,
}: {
  slide: GuideSlide;
  step: GuideStep;
  stepNumber?: number;
  stepCount?: number;
  trackingName: string;
  printMode?: boolean;
}) => {
  const light = slide.tone === "paper";
  const compactLayout = slide.compactLayout ?? false;
  const prompts = (slide.promptIndexes ?? step.prompts?.map((_, index) => index) ?? [])
    .map((index) => step.prompts?.[index])
    .filter((prompt): prompt is GuidePrompt => Boolean(prompt));
  return (
    <div className={`mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center ${compactLayout ? "py-3 sm:py-5" : "py-7 sm:py-10"}`}>
      <p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-300"}`}>
        {slide.eyebrow ?? `Step ${stepNumber} of ${stepCount} · Run it`}
      </p>
      <h1 className={`max-w-5xl font-serif font-semibold leading-tight tracking-[-0.04em] ${compactLayout ? "mt-3 text-3xl sm:text-4xl lg:text-5xl" : "mt-4 text-4xl sm:text-5xl lg:text-6xl"}`}>
        {slide.heading ?? slide.title}
      </h1>
      <div className={`grid ${compactLayout ? "mt-5 gap-4" : "mt-7 gap-6"} ${prompts.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-[0.72fr_1.28fr] lg:items-start"}`}>
        {prompts.length === 1 && (
          <div>
            <p className={`mb-4 text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-slate-500" : "text-slate-400"}`}>Do this</p>
            <ActionList step={step} indexes={slide.actionIndexes} light={light} />
            {slide.showNote && step.note && (
              <p className={`mt-6 rounded-2xl border-l-4 border-orange-500 p-4 text-sm leading-relaxed ${light ? "bg-orange-50 text-orange-950" : "bg-white/[0.06] text-slate-200"}`}><strong>Remember:</strong> {step.note}</p>
            )}
          </div>
        )}
        {prompts.map((prompt) => (
          <PromptCard key={prompt.label} prompt={prompt} stepId={step.id} trackingName={trackingName} printMode={printMode} light={light} compact={compactLayout} />
        ))}
      </div>
      {prompts.length > 1 && <div className="mt-6 max-w-4xl"><ActionList step={step} indexes={slide.actionIndexes} light={light} /></div>}
      {slide.showDeliverables && step.deliverables && <div className="mt-6"><Deliverables items={step.deliverables} light={light} /></div>}
    </div>
  );
};

const SlideContent = ({
  slide,
  steps,
  trackingName,
  cover,
  outcomes,
  roadmapHeading,
  finish,
  onStart,
  onGoToStep,
  renderCustomSlide,
  printMode = false,
}: SlideContentProps) => {
  const step = steps.find((candidate) => candidate.id === slide.stepId);
  const stepNumber = step ? steps.findIndex((candidate) => candidate.id === step.id) + 1 : 0;
  const light = slide.tone === "paper";
  const Icon = step?.icon ?? Sparkles;

  if (slide.kind === "cover") {
    return (
      <div className="relative mx-auto flex min-h-full w-full max-w-6xl items-center py-8 sm:py-12">
        <div className="grid w-full gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              {cover.logo ? (
                <div className={`flex w-fit items-center rounded-xl border bg-white px-4 py-2 shadow-lg ${light ? "border-slate-200 shadow-slate-950/10" : "border-white/15 shadow-black/20"} ${cover.logo.wrapperClassName ?? ""}`}>
                  <img
                    src={cover.logo.src}
                    alt={cover.logo.alt}
                    className={cover.logo.imageClassName ?? "h-8 w-auto sm:h-9"}
                  />
                </div>
              ) : null}
              <div className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold ${light ? "border-orange-500/25 bg-orange-50 text-orange-700" : "border-orange-400/30 bg-orange-400/10 text-orange-300"}`}>
                <BookOpen className="h-4 w-4 shrink-0" /> {cover.kicker ?? "Free, step-by-step slide guide"}
              </div>
            </div>
            <h1 className={`mt-7 max-w-5xl font-serif text-5xl font-semibold leading-[0.94] tracking-[-0.045em] sm:text-6xl lg:text-7xl xl:text-8xl ${light ? "text-slate-950" : "text-white"}`}>
              {cover.title}
              {cover.accent ? <span className={`mt-3 block ${light ? "text-[#0047FF]" : "text-orange-400"} ${cover.accentClassName ?? ""}`}>{cover.accent}</span> : null}
            </h1>
            {!cover.minimal ? (
              <>
                <p className={`mt-6 max-w-2xl text-lg leading-relaxed sm:text-xl ${light ? "text-slate-600" : "text-slate-300"}`}>
                  {cover.description}
                </p>
                <div className={`mt-7 flex flex-wrap gap-2 text-sm font-semibold ${light ? "text-slate-700" : "text-slate-200"}`}>
                  {cover.badges.map((badge) => (
                    <span key={badge} className={`rounded-full border px-4 py-2 ${light ? "border-slate-200 bg-white" : "border-white/15 bg-white/[0.06]"}`}>
                      {badge}
                    </span>
                  ))}
                </div>
                {!printMode ? (
                  <button
                    type="button"
                    onClick={onStart}
                    className={`mt-8 inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-orange-500 px-7 text-base font-bold text-white transition-colors hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:ring-offset-2 sm:text-lg ${light ? "focus-visible:ring-offset-[#faf8f5]" : "focus-visible:ring-offset-[#020817]"}`}
                  >
                    Start or resume the guide <ArrowRight className="h-5 w-5" />
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
          <div className={`rounded-3xl border p-6 shadow-2xl backdrop-blur sm:p-8 ${light ? "border-slate-200 bg-white/90 text-slate-950 shadow-slate-950/10" : "border-white/15 bg-white/[0.07] shadow-black/35"}`}>
            {!cover.hideFinishLineEyebrow ? <p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-300"}`}>The finish line</p> : null}
            <div className={`flex items-start gap-4 ${cover.hideFinishLineEyebrow ? "mt-0" : "mt-6"}`}>
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white">
                <FolderKanban className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-serif text-3xl font-semibold tracking-[-0.03em]">{cover.finishLineTitle}</h2>
                <p className={`mt-3 leading-relaxed ${light ? "text-slate-600" : "text-slate-400"}`}>{cover.finishLineBody}</p>
              </div>
            </div>
            {!cover.minimal ? (
              <>
                <div className="mt-7 h-2 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full w-[5%] rounded-full bg-gradient-to-r from-orange-500 to-amber-300" />
                </div>
                <p className="mt-3 text-sm text-slate-400">Progress is saved in this browser.</p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (slide.kind === "outcomes") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">{outcomes.eyebrow}</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
          {outcomes.heading}
        </h1>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.items.map((item, index) => (
            <div key={item} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <span className="text-xs font-extrabold tracking-[0.15em] text-orange-700">
                {String(index + 1).padStart(2, "0")}
              </span>
              <p className="mt-4 text-lg font-bold leading-snug text-slate-900">{item}</p>
            </div>
          ))}
        </div>
        <p className="mt-7 max-w-4xl text-lg leading-relaxed text-slate-600">{outcomes.summary}</p>
      </div>
    );
  }

  if (slide.kind === "roadmap") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The guide</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
          {roadmapHeading}
        </h1>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((roadmapStep, index) => {
            const StepIcon = roadmapStep.icon;
            return (
              <button
                key={roadmapStep.id}
                type="button"
                onClick={() => onGoToStep(roadmapStep.id)}
                disabled={printMode}
                className="group flex items-start gap-4 rounded-2xl border border-white/15 bg-white/[0.05] p-4 text-left transition-colors hover:border-orange-400/70 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-default"
              >
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 text-orange-300">
                  <StepIcon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">
                    Step {index + 1} · {roadmapStep.duration}
                  </span>
                  <span className="mt-1 block text-base font-bold leading-snug text-white">{roadmapStep.title}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (slide.kind === "custom") {
    return renderCustomSlide?.(slide, { light, printMode }) ?? null;
  }

  if (slide.kind === "finish") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <div className={`flex h-16 w-16 items-center justify-center rounded-3xl ring-1 ${light ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-emerald-400/15 text-emerald-300 ring-emerald-300/30"}`}>
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <p className={`mt-6 text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-300"}`}>{finish.eyebrow}</p>
        <h1 className={`mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl ${light ? "text-slate-950" : "text-white"}`}>
          {finish.heading}
        </h1>
        <p className={`mt-4 max-w-3xl text-lg leading-relaxed ${light ? "text-slate-600" : "text-slate-300"}`}>{finish.body}</p>
        <div className={`mt-8 grid gap-4 ${finish.cards.length > 2 ? "md:grid-cols-3" : "sm:grid-cols-2"}`}>
          {finish.cards.map((card, index) => (
            <a
              key={card.href}
              href={card.href}
              onClick={() => trackClick(`${trackingName} Graduation Path`, { destination: card.href, completed: true })}
              className={`group rounded-2xl border p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${index === 0 ? "border-orange-400 bg-orange-500 text-white" : light ? "border-slate-200 bg-white text-slate-950 shadow-sm hover:border-orange-400" : "border-white/15 bg-white/[0.05] text-white hover:border-orange-400/70 hover:bg-white/[0.09]"}`}
            >
              <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${index === 0 ? "text-orange-100" : light ? "text-orange-700" : "text-orange-300"}`}>{card.eyebrow}</p>
              <h2 className="mt-4 text-xl font-bold">{card.title}</h2>
              <p className={`mt-2 text-sm leading-relaxed ${index === 0 ? "text-orange-50" : light ? "text-slate-600" : "text-slate-400"}`}>{card.body}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold">Open training <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (!step) return null;

  if (slide.kind === "prompt") {
    return <GuidePromptSlide slide={slide} step={step} stepNumber={stepNumber} stepCount={steps.length} trackingName={trackingName} printMode={printMode} />;
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${light ? "bg-slate-950 text-white" : "bg-orange-500 text-white"}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className={`text-xs font-extrabold uppercase tracking-[0.17em] ${light ? "text-orange-700" : "text-orange-300"}`}>Step {stepNumber} of {steps.length} · {step.duration}</p>
          <p className={`mt-1 text-sm ${light ? "text-slate-500" : "text-slate-400"}`}>{step.description}</p>
        </div>
      </div>
      <h1 className="mt-5 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">{slide.heading ?? slide.title}</h1>
      {slide.showWhy && <p className={`mt-4 max-w-4xl text-base leading-relaxed sm:text-lg ${light ? "text-slate-600" : "text-slate-300"}`}>{step.whyItMatters}</p>}
      <div className={`mt-7 grid gap-7 ${slide.showImages && step.images?.length ? "lg:grid-cols-[0.82fr_1.18fr] lg:items-center" : "lg:grid-cols-[1fr_0.7fr]"}`}>
        <div>
          <p className={`mb-4 text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-slate-500" : "text-slate-400"}`}>Do this</p>
          <ActionList step={step} indexes={slide.actionIndexes} light={light} />
          {slide.showNote && step.note && <p className={`mt-6 rounded-2xl border-l-4 border-orange-500 p-4 text-sm leading-relaxed ${light ? "bg-orange-50 text-orange-950" : "bg-white/[0.06] text-slate-200"}`}><strong>Remember:</strong> {step.note}</p>}
        </div>
        <div className="min-w-0 space-y-5">
          {slide.showImages && step.images && <ImageGrid images={step.images} />}
          {slide.showDeliverables && step.deliverables && <Deliverables items={step.deliverables} light={light} />}
          {slide.showReference && step.reference && <ReferenceTable reference={step.reference} light={light} />}
        </div>
      </div>
      {slide.showLinks && step.links && !printMode && (
        <div className="mt-6 flex flex-wrap gap-3">
          {step.links.map((link) => {
            const external = link.external ?? /^https?:\/\//.test(link.href);
            return (
              <a
                key={`${link.href}-${link.label}`}
                href={link.href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                download={link.download || undefined}
                onClick={() => link.eventName
                  ? trackEvent(link.eventName, { step: step.id })
                  : trackClick(`${trackingName} Official Resource`, { step: step.id, label: link.label })}
                className={`inline-flex min-h-11 items-center gap-2 rounded-full px-5 py-2 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${light ? "bg-slate-950 text-white hover:bg-orange-600" : "bg-white text-slate-950 hover:bg-orange-50"}`}
              >
                {link.label}{link.download ? <ArrowRight className="h-4 w-4" /> : external ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const SlideGuide = ({
  name,
  trackingName,
  canonicalPath,
  storageKey,
  slideStorageKey,
  additionalStorageKeys = EMPTY_STORAGE_KEYS,
  seo,
  schema,
  cover,
  outcomes,
  roadmapHeading,
  steps,
  slides: authoredSlides,
  finish,
  branding,
  contentsExtras = [],
  centerSlideContent = false,
  audienceView = false,
  renderCustomSlide,
}: SlideGuideProps) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [contentsOpen, setContentsOpen] = useState(false);
  const [resumeSlideId, setResumeSlideId] = useState<string | null>(null);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const stageRef = useRef<HTMLElement>(null);
  const slideRef = useRef<HTMLElement>(null);
  const touchStart = useRef({ x: 0, y: 0 });
  // Older curriculum data can still carry checkpoint records. Keep those records
  // out of every learner-facing guide, including navigation and printed output.
  const slides = useMemo(
    () => authoredSlides.filter((slide) => slide.kind !== "checkpoint"),
    [authoredSlides],
  );
  const activeSlide = slides[activeSlideIndex];
  const lightChrome = branding?.chrome === "light";
  const chromeButtonClasses = lightChrome
    ? "border-slate-950/10 bg-slate-950/[0.04] text-slate-800 hover:border-orange-400 hover:bg-slate-950/[0.07]"
    : "border-white/15 bg-white/[0.06] text-white hover:border-orange-400 hover:bg-white/10";
  const activeStepNumber = activeSlide.stepId ? steps.findIndex((step) => step.id === activeSlide.stepId) + 1 : 0;
  const slideProgress = Math.round(((activeSlideIndex + 1) / slides.length) * 100);
  const guideLogo = branding?.logo ?? {
    src: "/orange-logo.png",
    alt: "Technical Leaders",
    href: "/ai-30/program",
    ariaLabel: "Back to the Longhand AI roadmap",
    imageClassName: "h-auto w-36 sm:w-44",
  };
  const paperBackdrop = branding?.paperBackdrop
    ?? "radial-gradient(circle at 8% 16%, rgba(255,116,32,.10), transparent 24%), radial-gradient(circle at 88% 82%, rgba(10,31,68,.06), transparent 25%)";
  const darkBackdrop = branding?.darkBackdrop
    ?? "radial-gradient(circle at 82% 18%, rgba(56,189,248,.12), transparent 26%), radial-gradient(circle at 10% 90%, rgba(255,116,32,.14), transparent 28%), linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)";
  const slideIndexByHash = useMemo(
    () => new Map(slides.map((slide, index) => [`#${slide.id}`, index])),
    [slides],
  );
  const firstSlideByStep = useMemo(() => {
    const firstIndexes = new Map<string, number>();
    slides.forEach((slide, index) => {
      if (slide.stepId && !firstIndexes.has(slide.stepId)) {
        firstIndexes.set(slide.stepId, index);
      }
    });
    return firstIndexes;
  }, [slides]);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description: schema.description,
    totalTime: schema.totalTime,
    ...(schema.supplies?.length ? { supply: schema.supplies.map((supply) => ({ "@type": "HowToSupply", name: supply })) } : {}),
    step: steps.map((step, index) => {
      const firstSlideIndex = firstSlideByStep.get(step.id);
      const firstSlideId = firstSlideIndex == null
        ? `step-${step.id}`
        : (slides[firstSlideIndex]?.id ?? `step-${step.id}`);
      return {
        "@type": "HowToStep",
        position: index + 1,
        name: step.title,
        text: `${step.description} ${step.actions.map((action) => typeof action === "string" ? action : `${action.text} ${action.subSteps.join(" ")}`).join(" ")}`,
        url: `https://www.technical-leaders.com${canonicalPath}#${firstSlideId}`,
      };
    }),
  };

  useEffect(() => {
    try {
      // Clear checkpoint completion data created by older versions of the guide.
      localStorage.removeItem(storageKey);
      const savedSlide = localStorage.getItem(slideStorageKey);
      if (savedSlide && slides.some((slide) => slide.id === savedSlide)) setResumeSlideId(savedSlide);
    } catch {
      // The guide still works for the current visit when storage is blocked.
    }

    const hashIndex = slideIndexByHash.get(window.location.hash) ?? -1;
    if (hashIndex >= 0) setActiveSlideIndex(hashIndex);
    setProgressLoaded(true);
    trackEvent(`${trackingName} Page View`, {
      total_steps: steps.length,
      total_slides: slides.length,
    });
  }, [slideIndexByHash, slideStorageKey, slides, steps, storageKey, trackingName]);

  useEffect(() => {
    const handleHashChange = () => {
      const hashIndex = slideIndexByHash.get(window.location.hash) ?? -1;
      if (hashIndex < 0) return;
      setActiveSlideIndex(hashIndex);
      setContentsOpen(false);
      window.requestAnimationFrame(() => {
        slideRef.current?.scrollTo({ top: 0 });
        slideRef.current?.focus({ preventScroll: true });
      });
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [slideIndexByHash]);

  useEffect(() => {
    if (!progressLoaded) return;
    try {
      if (activeSlideIndex > 0) {
        localStorage.setItem(slideStorageKey, activeSlide.id);
        setResumeSlideId(activeSlide.id);
      }
    } catch {
      // Keep all behavior in memory when storage is unavailable.
    }
    if (window.location.hash !== `#${activeSlide.id}`) window.history.replaceState(null, "", `#${activeSlide.id}`);
    trackEvent(`${trackingName} Slide Viewed`, {
      slide: activeSlide.id,
      slide_number: activeSlideIndex + 1,
      step: activeSlide.stepId ?? null,
      step_number: activeStepNumber || null,
    });
  }, [activeSlide.id, activeSlide.stepId, activeSlideIndex, activeStepNumber, progressLoaded, slideStorageKey, trackingName]);

  const resumeTargetIndex = useMemo(() => {
    if (!resumeSlideId) return 1;
    if (resumeSlideId) {
      const savedIndex = slides.findIndex((slide) => slide.id === resumeSlideId);
      if (savedIndex > 0) return savedIndex;
    }
    return 1;
  }, [resumeSlideId, slides]);

  const goToSlide = (nextIndex: number) => {
    const boundedIndex = Math.min(Math.max(nextIndex, 0), slides.length - 1);
    setActiveSlideIndex(boundedIndex);
    setContentsOpen(false);
    window.requestAnimationFrame(() => {
      slideRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      slideRef.current?.focus({ preventScroll: true });
    });
  };

  const goToStep = (stepId: string) => {
    const targetIndex = firstSlideByStep.get(stepId) ?? -1;
    if (targetIndex >= 0) goToSlide(targetIndex);
  };

  const goNext = () => goToSlide(activeSlideIndex + 1);

  const startOrResume = () => {
    trackClick(`Start or Resume ${trackingName}`, {
      target_slide: slides[resumeTargetIndex]?.id,
    });
    goToSlide(resumeTargetIndex > 0 ? resumeTargetIndex : 1);
  };

  const resetProgress = () => {
    setResumeSlideId(null);
    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(slideStorageKey);
      additionalStorageKeys.forEach((key) => localStorage.removeItem(key));
    } catch {
      // The in-memory reset still succeeds.
    }
    trackEvent(`${trackingName} Guide Restarted`);
    goToSlide(0);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await stageRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      // Fullscreen may be blocked by the browser.
    }
    if (audienceView) slideRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!audienceView || !contentsOpen) return;
    const dialog = stageRef.current?.querySelector<HTMLElement>(".slide-guide-contents");
    const buttons = dialog?.querySelectorAll<HTMLButtonElement>("button");
    if (!dialog || !buttons?.length) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const slideElement = slideRef.current;
    first.focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener("keydown", trapFocus);
    return () => {
      dialog.removeEventListener("keydown", trapFocus);
      slideElement?.focus({ preventScroll: true });
    };
  }, [audienceView, contentsOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (audienceView && (event.altKey || event.ctrlKey || event.metaKey || target?.isContentEditable)) return;
      if (event.key === "Escape") { setContentsOpen(false); return; }
      if (contentsOpen || target?.closest(audienceView ? "input, textarea, select, [role='dialog']" : "button, a, input, textarea, select, [role='dialog']")) return;
      if (audienceView && event.key === " " && target?.closest("button, a")) return;
      if (["ArrowRight", "PageDown", " "].includes(event.key)) { event.preventDefault(); if (activeSlideIndex < slides.length - 1) goNext(); }
      else if (["ArrowLeft", "PageUp"].includes(event.key)) { event.preventDefault(); goToSlide(activeSlideIndex - 1); }
      else if (event.key === "Home") { event.preventDefault(); goToSlide(0); }
      else if (event.key === "End") { event.preventDefault(); goToSlide(slides.length - 1); }
      else if (event.key.toLowerCase() === "f") void toggleFullscreen();
      else if (event.key.toLowerCase() === "c" || event.key === "?") setContentsOpen(true);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const sharedSlideProps = {
    steps,
    trackingName,
    cover,
    outcomes,
    roadmapHeading,
    finish,
    onStart: startOrResume,
    onGoToStep: goToStep,
    renderCustomSlide,
  };

  return (
    <div className={`min-h-screen bg-black font-sans print:bg-white ${branding?.rootClassName ?? ""}`}>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} image={seo.image} author={seo.author} structuredData={schemaData} />
      <style dangerouslySetInnerHTML={{ __html: `@media print { @page { size: 13.333in 7.5in; margin: 0; } html, body { margin: 0 !important; background: #fff !important; } }` }} />

      <main ref={stageRef} className="slide-guide-stage relative mx-auto flex h-[100svh] w-full max-w-[1920px] flex-col overflow-hidden bg-[#020817] text-white print:hidden">
        {audienceView && <p className="sr-only">Use Left and Right arrow keys or Page Up and Page Down to navigate. Home opens the first slide; End opens the last. Press C for contents, F for fullscreen, and Escape to close contents. On touch screens, swipe left or right.</p>}
        <header className={`slide-guide-header slide-guide-chrome relative z-30 flex h-16 flex-shrink-0 items-center justify-between border-b px-4 backdrop-blur sm:px-6 lg:px-8 ${audienceView ? "slide-guide-audience-header" : ""} ${lightChrome ? "border-slate-950/10 bg-[#faf8f5] text-slate-950" : "border-white/10 bg-[#020817]/95"}`}>
          <a
            href={guideLogo.href ?? "/ai-30/program"}
            className={`inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${guideLogo.wrapperClassName ?? ""}`}
            aria-label={guideLogo.ariaLabel ?? `Open ${guideLogo.alt}`}
          >
            <img src={guideLogo.src} alt={guideLogo.alt} className={guideLogo.imageClassName ?? "h-auto w-36 sm:w-44"} />
          </a>
          <div className="flex items-center gap-2 sm:gap-3">
            {!audienceView && activeStepNumber > 0 && <span className={`hidden text-sm font-bold sm:inline ${lightChrome ? "text-slate-600" : "text-slate-300"}`}>Step {activeStepNumber} of {steps.length}</span>}
            <span className={`slide-guide-slide-count rounded-full border px-3 py-1.5 text-xs font-extrabold tracking-[0.08em] sm:text-sm ${lightChrome ? "border-slate-950/10 bg-slate-950/[0.04] text-slate-700" : "border-white/15 bg-white/[0.06] text-slate-200"}`}>{activeSlideIndex + 1} / {slides.length}</span>
            {!audienceView && <><button type="button" onClick={() => setContentsOpen(true)} aria-label="Contents" className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:px-4 ${chromeButtonClasses}`}><Menu className="h-4 w-4" /><span className="hidden sm:inline">Contents</span></button>
            <button type="button" onClick={() => window.print()} className={`hidden h-10 w-10 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 md:inline-flex ${chromeButtonClasses}`} aria-label="Print or save this guide as a PDF"><Printer className="h-4 w-4" /></button>
            <button type="button" onClick={() => void toggleFullscreen()} className={`hidden h-10 w-10 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 md:inline-flex ${chromeButtonClasses}`} aria-label="Toggle fullscreen"><Maximize2 className="h-4 w-4" /></button></>}
          </div>
        </header>

        <section
          ref={slideRef}
          data-slide-tone={activeSlide.tone}
          tabIndex={-1}
          aria-label={`${activeSlide.title}. Slide ${activeSlideIndex + 1} of ${slides.length}`}
          onTouchStart={(event) => { const touch = event.changedTouches[0]; touchStart.current = { x: touch.clientX, y: touch.clientY }; }}
          onTouchEnd={(event) => { const touch = event.changedTouches[0]; const deltaX = touch.clientX - touchStart.current.x; const deltaY = touch.clientY - touchStart.current.y; if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return; if (deltaX < 0 && activeSlideIndex < slides.length - 1) goNext(); if (deltaX > 0) goToSlide(activeSlideIndex - 1); }}
          className={`relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 outline-none sm:px-8 lg:px-12 ${TONE_CLASSES[activeSlide.tone]}`}
        >
          <div className="pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" style={{ backgroundImage: activeSlide.tone === "paper" ? paperBackdrop : darkBackdrop, backgroundSize: activeSlide.tone === "paper" ? "auto" : "auto, auto, 48px 48px, 48px 48px" }} />
          <div
            key={activeSlide.id}
            data-content-alignment={centerSlideContent ? "center" : "default"}
            className={`relative min-h-full animate-in fade-in slide-in-from-bottom-2 duration-300 ${centerSlideContent ? "h-full" : ""}`}
          >
            <SlideContent slide={activeSlide} {...sharedSlideProps} />
          </div>
        </section>

        {!audienceView && <footer className={`slide-guide-footer slide-guide-chrome relative z-30 flex min-h-[76px] flex-shrink-0 items-center gap-3 border-t px-4 sm:px-6 lg:px-8 ${lightChrome ? "border-slate-950/10 bg-[#faf8f5] text-slate-950" : "border-white/10 bg-[#020817]"}`}>
          <button type="button" onClick={() => goToSlide(activeSlideIndex - 1)} disabled={activeSlideIndex === 0} aria-label="Back" className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-not-allowed disabled:opacity-30 ${lightChrome ? "border-slate-950/15 text-slate-800 hover:border-orange-400 hover:bg-slate-950/[0.05]" : "border-white/15 text-white hover:border-orange-400 hover:bg-white/[0.06]"}`}><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Back</span></button>
          <div className="min-w-0 flex-1"><div className="flex items-center justify-between gap-4 text-xs"><span className={`truncate font-semibold ${lightChrome ? "text-slate-500" : "text-slate-400"}`}>{activeSlide.title}</span><span className={`slide-guide-progress-label font-bold ${lightChrome ? "text-orange-700" : "text-orange-300"}`}>{slideProgress}%</span></div><div className={`slide-guide-progress-track mt-2 h-1.5 overflow-hidden rounded-full ${lightChrome ? "bg-slate-950/10" : "bg-white/10"}`} aria-hidden="true"><div className={`slide-guide-progress-fill h-full rounded-full transition-all duration-300 ${lightChrome ? "bg-orange-500" : "bg-gradient-to-r from-orange-500 to-amber-300"}`} style={{ width: `${slideProgress}%` }} /></div></div>
          {activeSlide.kind === "finish" || activeSlideIndex === slides.length - 1 ? (
            <a href={branding?.exit?.href ?? "/ai-30/program"} className={`slide-guide-primary-action inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:px-5 ${lightChrome ? "bg-orange-500 text-white hover:bg-orange-600" : "bg-white text-slate-950 hover:bg-orange-50"}`}><span className="hidden sm:inline">{branding?.exit?.label ?? "Back to roadmap"}</span><span className="sm:hidden">Exit</span><ArrowRight className="h-4 w-4" /></a>
          ) : (
            <button type="button" onClick={goNext} className="slide-guide-primary-action inline-flex min-h-11 items-center gap-2 rounded-full bg-orange-500 px-4 text-sm font-bold text-white transition-colors hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:px-5"><span className="max-w-[140px] truncate sm:max-w-none">{activeSlide.nextLabel ?? "Continue"}</span><ArrowRight className="h-4 w-4 flex-shrink-0" /></button>
          )}
        </footer>}

        {contentsOpen && (
          <div className={`slide-guide-contents slide-guide-chrome absolute inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl sm:p-8 ${lightChrome ? "bg-slate-950/20" : "bg-[#020817]/95"}`} role="dialog" aria-modal="true" aria-labelledby="contents-title" onMouseDown={(event) => { if (event.target === event.currentTarget) setContentsOpen(false); }}>
            <div className={`slide-guide-contents-panel flex max-h-[90svh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border shadow-2xl ${lightChrome ? "border-slate-200 bg-[#faf8f5] text-slate-950 shadow-slate-950/20" : "border-white/15 bg-[#0a1f44] shadow-black/50"}`}>
              <div className={`flex items-start justify-between gap-4 border-b p-5 sm:p-7 ${lightChrome ? "border-slate-200" : "border-white/10"}`}>
                <div><p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${lightChrome ? "text-orange-700" : "text-orange-300"}`}>{name}</p><h2 id="contents-title" className={`mt-2 font-serif text-3xl font-semibold sm:text-4xl ${lightChrome ? "text-slate-950" : "text-white"}`}>{branding?.contentsTitle ?? `Your ${steps.length}-step roadmap`}</h2><p className={`mt-2 text-sm ${lightChrome ? "text-slate-600" : "text-slate-400"}`}>Slide {activeSlideIndex + 1} of {slides.length}</p></div>
                <button type="button" onClick={() => setContentsOpen(false)} className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${lightChrome ? "border-slate-200 text-slate-800 hover:border-orange-400 hover:bg-slate-100" : "border-white/15 text-white hover:border-orange-400 hover:bg-white/10"}`} aria-label="Close contents"><X className="h-5 w-5" /></button>
              </div>
              <div className="overflow-y-auto p-5 sm:p-7">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const active = activeSlide.stepId === step.id;
                    return (
                      <button key={step.id} type="button" onClick={() => goToStep(step.id)} className={`flex items-start gap-4 rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${active ? "border-orange-400 bg-orange-400/10" : lightChrome ? "border-slate-200 bg-white hover:border-orange-400" : "border-white/10 bg-white/[0.04] hover:border-white/25 hover:bg-white/[0.07]"}`}>
                        <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${active ? "bg-orange-500 text-white" : lightChrome ? "bg-slate-100 text-orange-700" : "bg-white/10 text-orange-300"}`}><StepIcon className="h-5 w-5" /></span>
                        <span className="min-w-0 flex-1"><span className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">Step {index + 1} · {step.duration}</span><span className={`mt-1 block text-base font-bold leading-snug ${lightChrome ? "text-slate-950" : "text-white"}`}>{step.title}</span></span>
                      </button>
                    );
                  })}
                </div>
                {contentsExtras.length > 0 && (
                  <div className={`mt-7 border-t pt-6 ${lightChrome ? "border-slate-200" : "border-white/10"}`}>
                    <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${lightChrome ? "text-orange-700" : "text-orange-300"}`}>Also in this guide</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {contentsExtras.map((extra) => (
                        <button key={extra.slideId} type="button" onClick={() => goToSlide(slides.findIndex((slide) => slide.id === extra.slideId))} className={`rounded-full border px-4 py-2 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${lightChrome ? "border-slate-200 bg-white text-slate-800 hover:border-orange-400 hover:bg-slate-50" : "border-white/15 bg-white/[0.05] text-white hover:border-orange-400 hover:bg-white/10"}`}>{extra.label}</button>
                      ))}
                    </div>
                  </div>
                )}
                <button type="button" onClick={resetProgress} className={`mt-7 inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${lightChrome ? "border-slate-200 text-slate-600 hover:border-red-400 hover:text-red-700" : "border-white/15 text-slate-300 hover:border-red-400 hover:text-white"}`}><RotateCcw className="h-4 w-4" /> Restart guide</button>
              </div>
            </div>
          </div>
        )}
      </main>

      <div className="hidden print:block">
        {slides.map((slide) => (
          <article key={`print-${slide.id}`} data-slide-tone={slide.tone} className={`relative h-[7.5in] w-[13.333in] overflow-hidden px-[0.65in] [break-after:page] ${TONE_CLASSES[slide.tone]}`} style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
            <SlideContent slide={slide} {...sharedSlideProps} printMode />
          </article>
        ))}
      </div>
    </div>
  );
};

export default SlideGuide;
